/**
 * Companio product page.
 *
 * THE THREE LOAD-BEARING MECHANISMS (handover §5) ARE UNTOUCHED:
 * - the form submits NATIVELY as multipart, because /cart/add.js cannot carry
 *   files and the customer's pet photo can only reach the order that way
 * - the gift box is recorded as a hidden `properties[_gift_box]` property and
 *   handed to the cart via return_to=/cart?companio_gift=1
 * - the cart identifies gift lines by `_gift_for`, written on its side
 * Do not turn any of this into fetch()/cart-drawer behaviour.
 *
 * The custom-mode inputs are kept `disabled` while in ready mode so the browser
 * omits them from the POST entirely — an enabled empty file input would
 * otherwise create a blank line item property.
 *
 * WINDOW C (v2 rebuild) CHANGED FOUR THINGS, ALL PRESENTATION:
 * 1. updateVariant() writes the button label into [data-add-label] rather than
 *    replacing the button's textContent. The artboard puts the price inside the
 *    button, so the button now holds a [data-price] span too — assigning to
 *    textContent would delete it on the first variant change.
 * 2. renderEmpty() draws the artboard's 48px upload glyph. The icon is INLINE
 *    SVG on purpose (handover §4.6): Figma asset URLs expire after 7 days and
 *    an inline icon inherits currentColor.
 * 3. bindGiftBox() drives two radios (no gift box / gift box) instead of a
 *    checkbox that expands. Choosing "gift box" opens the same picker, because
 *    the store carries three boxes and one radio cannot say which. The hidden
 *    input, its disabled-when-empty rule and the return_to handoff are
 *    byte-for-byte the same as before.
 * 4. bindDetailTabs() is new — the details/description/shipping tabs.
 *
 * The size-guide, gallery, upload, faces, quantity and submit paths are
 * unchanged.
 */

const ACCEPTED = ['jpg', 'jpeg', 'png', 'heic', 'heif'];
const MAX_BYTES = 20 * 1024 * 1024; // Shopify's per-file limit for line item uploads.
const SIZE_HINT = 'JPG, PNG or HEIC · up to 20 MB';

class CompanioProduct extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants || '[]');
    } catch (error) {
      this.variants = [];
    }

    this.form = this.querySelector('.companio-product-form');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.quantityValue = this.querySelector('[data-quantity-value]');
    this.customFields = this.querySelector('[data-custom-fields]');
    this.trustList = this.querySelector('[data-trust-list]');
    this.segmented = this.querySelector('[data-mode-segmented]');
    this.modeProperty = this.querySelector('[data-mode-property]');
    this.facesProperty = this.querySelector('[data-faces-property]');
    this.giftInput = this.querySelector('[data-gift-input]');

    this.selected = {};
    this.querySelectorAll('[data-option-position].active').forEach((button) => {
      this.selected[button.dataset.optionPosition] = button.dataset.optionValue;
    });

    this.mode = 'ready';
    this.quantity = 1;

    this.bindOptions();
    this.bindMode();
    this.bindFaces();
    this.bindQuantity();
    this.bindName();
    this.bindUploads();
    this.bindGallery();
    this.bindSizeGuide();
    this.bindGiftBox();
    this.bindDetailTabs();
    this.bindStickyBar();
    this.bindSubmit();

    this.updateVariant();
  }

  /* ---------------------------------------------------------------- variants */

  bindOptions() {
    this.querySelectorAll('[data-option-position]').forEach((button) => {
      button.addEventListener('click', () => {
        const position = button.dataset.optionPosition;
        this.selected[position] = button.dataset.optionValue;

        const group = button.parentElement;
        group.querySelectorAll('[data-option-position]').forEach((sibling) => {
          const active = sibling === button;
          sibling.classList.toggle('active', active);
          if (sibling.hasAttribute('role')) sibling.setAttribute('aria-checked', String(active));
        });

        const colorName = this.querySelector('[data-color-name]');
        if (colorName && button.classList.contains('pdp-swatch')) {
          colorName.textContent = button.dataset.optionValue;
        }

        this.clearError('[data-size-error]');
        this.updateVariant();
      });
    });
  }

  matchVariant() {
    const positions = Object.keys(this.selected);
    return this.variants.find((variant) =>
      positions.every((position) => variant.options[Number(position) - 1] === this.selected[position])
    );
  }

  updateVariant() {
    const variant = this.matchVariant();

    if (variant) {
      if (this.variantInput) this.variantInput.value = variant.id;
      this.setPrice(variant.price);
      this.setStickySummary(variant.title);
    }

    // Cross out sizes that are unavailable in the chosen colour.
    this.querySelectorAll('[data-size-options] [data-option-position]').forEach((button) => {
      const trial = Object.assign({}, this.selected);
      trial[button.dataset.optionPosition] = button.dataset.optionValue;
      const candidate = this.variants.find((v) =>
        Object.keys(trial).every((p) => v.options[Number(p) - 1] === trial[p])
      );
      button.classList.toggle('sold-out', !candidate || !candidate.available);
    });

    const addButton = this.querySelector('[data-add-button]');
    if (addButton) {
      const available = Boolean(variant && variant.available);
      addButton.disabled = !available;

      // Write into the label span, NOT the button's textContent — the price
      // span lives inside this button and textContent would wipe it out.
      const label = addButton.querySelector('[data-add-label]');
      const text = available ? addButton.dataset.label : 'Sold out';
      if (label) {
        label.textContent = text;
      } else {
        addButton.textContent = text;
      }
    }
  }

  setPrice(cents) {
    const text = this.formatMoney(cents);
    document.querySelectorAll('[data-price]').forEach((node) => {
      node.textContent = text;
    });
  }

  formatMoney(cents) {
    const format = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: format,
      }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  setStickySummary(title) {
    const node = document.querySelector('[data-sticky-summary]');
    if (node) node.textContent = title;
  }

  /* ------------------------------------------------------------------- mode */

  bindMode() {
    if (!this.segmented) return;

    this.segmented.querySelectorAll('[data-mode-set]').forEach((button) => {
      button.addEventListener('click', () => this.setMode(button.dataset.modeSet));
    });
  }

  setMode(mode) {
    this.mode = mode;
    const custom = mode === 'custom';

    this.segmented.dataset.mode = mode;
    this.segmented.querySelectorAll('[data-mode-set]').forEach((button) => {
      button.setAttribute('aria-selected', String(button.dataset.modeSet === mode));
    });

    if (this.customFields) this.customFields.hidden = !custom;
    if (this.trustList) this.trustList.hidden = !custom;

    // Only submit the personalisation fields when they are actually in play.
    if (this.facesProperty) this.facesProperty.disabled = !custom;
    this.querySelectorAll('[data-upload-input]').forEach((input) => {
      const slot = input.closest('.upload-item');
      input.disabled = !custom || slot.hidden;
    });

    if (this.modeProperty) {
      const label = this.segmented.querySelector('[data-mode-set="' + mode + '"]');
      this.modeProperty.value = label ? label.textContent.trim() : mode;
    }
  }

  bindFaces() {
    this.querySelectorAll('[data-faces]').forEach((button) => {
      button.addEventListener('click', () => {
        const faces = Number(button.dataset.faces);

        this.querySelectorAll('[data-faces]').forEach((sibling) => {
          sibling.classList.toggle('active', sibling === button);
        });

        if (this.facesProperty) this.facesProperty.value = String(faces);

        const second = this.querySelector('.upload-item[data-slot="1"]');
        if (second) {
          second.hidden = faces < 2;
          const input = second.querySelector('[data-upload-input]');
          if (input) input.disabled = faces < 2 || this.mode !== 'custom';
        }
      });
    });
  }

  /* --------------------------------------------------------------- quantity */

  bindQuantity() {
    this.querySelectorAll('[data-quantity]').forEach((button) => {
      button.addEventListener('click', () => {
        this.quantity = Math.max(1, this.quantity + Number(button.dataset.quantity));
        if (this.quantityValue) this.quantityValue.textContent = this.quantity;
        if (this.quantityInput) this.quantityInput.value = this.quantity;
      });
    });
  }

  bindName() {
    const input = this.querySelector('[data-name-input]');
    const counter = this.querySelector('[data-name-counter]');
    if (!input || !counter) return;

    const max = input.maxLength;
    input.addEventListener('input', () => {
      counter.textContent = input.value.length + '/' + max;
      counter.classList.toggle('limit', input.value.length >= max);
    });
  }

  /* ---------------------------------------------------------------- uploads */

  bindUploads() {
    this.querySelectorAll('[data-upload-zone]').forEach((zone) => {
      const input = zone.querySelector('[data-upload-input]');
      const content = zone.querySelector('[data-upload-content]');
      const error = zone.parentElement.querySelector('[data-upload-error]');

      this.renderEmpty(content);

      zone.addEventListener('click', (event) => {
        if (event.target.closest('.file-actions')) return;
        input.click();
      });

      zone.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          input.click();
        }
      });

      ['dragenter', 'dragover'].forEach((name) => {
        zone.addEventListener(name, (event) => {
          event.preventDefault();
          zone.classList.add('dragover');
        });
      });

      ['dragleave', 'drop'].forEach((name) => {
        zone.addEventListener(name, () => zone.classList.remove('dragover'));
      });

      zone.addEventListener('drop', (event) => {
        event.preventDefault();
        if (!event.dataTransfer.files.length) return;
        input.files = event.dataTransfer.files;
        input.dispatchEvent(new Event('change'));
      });

      input.addEventListener('change', () => {
        const file = input.files && input.files[0];
        if (!file) {
          this.renderEmpty(content);
          zone.classList.remove('success', 'error');
          return;
        }

        const extension = (file.name.split('.').pop() || '').toLowerCase();

        if (!ACCEPTED.includes(extension)) {
          this.showUploadError(zone, error, input, content, 'That file type is not supported. Use JPG, PNG or HEIC.');
          return;
        }

        if (file.size > MAX_BYTES) {
          this.showUploadError(zone, error, input, content, 'That file is over 20 MB. Try a smaller photo.');
          return;
        }

        zone.classList.remove('error');
        if (error) {
          error.textContent = '';
          error.classList.remove('visible');
        }
        this.renderSuccess(zone, content, input, file);
      });
    });
  }

  renderEmpty(content) {
    content.innerHTML =
      '<div class="upload-empty">' +
      '<svg class="upload-icon" viewBox="0 0 48 48" width="48" height="48" aria-hidden="true" focusable="false">' +
      '<circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" stroke-width="2"/>' +
      '<path d="M24 15v18M15 24h18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
      '</svg>' +
      '<strong>Choose a photo</strong>' +
      '<span>' + SIZE_HINT + '</span>' +
      '</div>';
  }

  renderSuccess(zone, content, input, file) {
    zone.classList.add('success');

    const wrap = document.createElement('div');
    wrap.className = 'upload-success';

    const thumb = document.createElement('div');
    thumb.className = 'upload-thumb';

    // HEIC has no browser preview, so fall back to a label rather than a broken image.
    if (/^image\/(jpeg|png)$/.test(file.type)) {
      const img = document.createElement('img');
      img.alt = '';
      img.src = URL.createObjectURL(file);
      img.addEventListener('load', () => URL.revokeObjectURL(img.src));
      thumb.appendChild(img);
    } else {
      thumb.textContent = 'HEIC';
    }

    const details = document.createElement('div');

    const name = document.createElement('p');
    name.className = 'file-name';
    name.textContent = file.name;

    const line = document.createElement('p');
    line.className = 'success-line';
    line.textContent = 'Ready to send';

    const actions = document.createElement('div');
    actions.className = 'file-actions';

    const replace = document.createElement('button');
    replace.type = 'button';
    replace.textContent = 'Replace';
    replace.addEventListener('click', (event) => {
      event.stopPropagation();
      input.click();
    });

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.textContent = 'Remove';
    remove.addEventListener('click', (event) => {
      event.stopPropagation();
      input.value = '';
      zone.classList.remove('success');
      this.renderEmpty(zone.querySelector('[data-upload-content]'));
    });

    actions.append(replace, remove);
    details.append(name, line, actions);
    wrap.append(thumb, details);

    content.innerHTML = '';
    content.appendChild(wrap);
  }

  showUploadError(zone, error, input, content, message) {
    input.value = '';
    zone.classList.add('error');
    zone.classList.remove('success');
    this.renderEmpty(content);
    if (error) {
      error.textContent = message;
      error.classList.add('visible');
    }
  }

  /* ---------------------------------------------------------------- gallery */

  bindGallery() {
    const section = this.closest('.companio-pdp') || document;
    const thumbs = section.querySelectorAll('[data-thumbs] .thumb');
    const slides = section.querySelectorAll('[data-slide]');
    const index = section.querySelector('[data-gallery-index]');
    if (!thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const target = thumb.dataset.index;
        thumbs.forEach((t) => t.classList.toggle('active', t === thumb));
        slides.forEach((slide) => slide.classList.toggle('active', slide.dataset.slide === target));
        if (index) index.textContent = Number(target) + 1 + ' / ' + slides.length;
      });
    });
  }

  /* ---------------------------------------------------------------- gift box */

  /**
   * The chosen box is only a hidden `_gift_box` property here: this form is a
   * multipart POST (it carries the photo files), so it can create exactly one
   * line item. `return_to` then tells the cart page to turn that property into
   * a real gift box line linked back with `_gift_for` — see companio-cart.js.
   *
   * The UI is two radios now, but the data path is identical to before.
   */
  bindGiftBox() {
    const row = this.querySelector('[data-gift-row]');
    if (!row || !this.giftInput) return;

    const modal = document.querySelector('[data-gift-modal]');
    const chooseRadio = row.querySelector('[data-gift-toggle]');
    const noneRadio = row.querySelector('[data-gift-remove]');
    const chosen = row.querySelector('[data-gift-chosen]');
    const thumb = row.querySelector('[data-gift-thumb]');
    const name = row.querySelector('[data-gift-name]');
    const price = row.querySelector('[data-gift-price]');

    const setRadio = (element, on) => {
      if (!element) return;
      element.classList.toggle('is-active', on);
      element.setAttribute('aria-checked', String(on));
    };

    const closeModal = () => {
      if (!modal) return;
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
    };

    const openModal = () => {
      if (!modal) return;
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
      const close = modal.querySelector('[data-gift-close]');
      if (close) close.focus();
    };

    // No selection: the input stays disabled so the browser omits it entirely.
    const clear = () => {
      this.giftInput.value = '';
      this.giftInput.disabled = true;
      setRadio(noneRadio, true);
      setRadio(chooseRadio, false);
      if (chosen) chosen.hidden = true;
      if (modal) modal.querySelectorAll('[data-gift-choose]').forEach((c) => c.classList.remove('active'));
    };

    const select = (card) => {
      this.giftInput.value = card.dataset.giftChoose;
      this.giftInput.disabled = false;
      setRadio(noneRadio, false);
      setRadio(chooseRadio, true);
      if (chosen) chosen.hidden = false;
      if (name) name.textContent = card.dataset.giftTitle || '';
      if (price) price.textContent = '+' + (card.dataset.giftAmount || '');
      if (thumb) {
        const image = card.querySelector('img');
        thumb.style.backgroundImage = image ? 'url(' + (image.currentSrc || image.src) + ')' : '';
      }
      if (modal) {
        modal.querySelectorAll('[data-gift-choose]').forEach((c) => c.classList.toggle('active', c === card));
      }
      closeModal();
    };

    if (noneRadio) noneRadio.addEventListener('click', clear);
    if (chooseRadio) chooseRadio.addEventListener('click', openModal);
    row.querySelectorAll('[data-gift-open]').forEach((button) => button.addEventListener('click', openModal));

    if (modal) {
      modal.querySelectorAll('[data-gift-choose]').forEach((card) => {
        card.addEventListener('click', () => select(card));
      });
      modal.querySelectorAll('[data-gift-close]').forEach((button) => button.addEventListener('click', closeModal));
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeModal();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeModal();
      });
    }

    // Stage two of the gift chain. Do not remove.
    if (this.form) {
      this.form.addEventListener('submit', () => {
        if (!this.giftInput.value) return;
        let returnTo = this.form.querySelector('input[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          this.form.appendChild(returnTo);
        }
        returnTo.value = '/cart?companio_gift=1';
      });
    }
  }

  /* ------------------------------------------------------------ detail tabs */

  bindDetailTabs() {
    const tabs = this.querySelector('[data-tabs]');
    if (!tabs) return;

    const buttons = Array.from(tabs.querySelectorAll('[data-tab-button]'));
    const panels = Array.from(tabs.querySelectorAll('[data-tab-panel]'));
    const rule = tabs.querySelector('[data-tab-rule]');
    if (!buttons.length) return;

    const moveRule = (button) => {
      if (!rule) return;
      rule.style.left = button.offsetLeft + 'px';
      rule.style.width = button.offsetWidth + 'px';
    };

    const activate = (button) => {
      buttons.forEach((other) => {
        const on = other === button;
        other.classList.toggle('is-active', on);
        other.setAttribute('aria-selected', String(on));
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.tabId !== button.dataset.tabTarget;
      });
      moveRule(button);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => activate(button));
    });

    // Arrow-key navigation, which a tablist is expected to support.
    tabs.addEventListener('keydown', (event) => {
      const index = buttons.indexOf(document.activeElement);
      if (index === -1) return;
      let next = null;
      if (event.key === 'ArrowRight') next = buttons[(index + 1) % buttons.length];
      if (event.key === 'ArrowLeft') next = buttons[(index - 1 + buttons.length) % buttons.length];
      if (!next) return;
      event.preventDefault();
      next.focus();
      activate(next);
    });

    moveRule(buttons.find((button) => button.classList.contains('is-active')) || buttons[0]);

    // Web fonts land after first paint and change the tab widths under the rule.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        const active = buttons.find((button) => button.classList.contains('is-active'));
        if (active) moveRule(active);
      });
    }
    window.addEventListener('resize', () => {
      const active = buttons.find((button) => button.classList.contains('is-active'));
      if (active) moveRule(active);
    });
  }

  bindSizeGuide() {
    const modal = document.querySelector('[data-size-modal]');
    if (!modal) return;

    const open = () => {
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
    };

    const close = () => {
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
    };

    this.querySelectorAll('[data-open-size-guide]').forEach((button) => button.addEventListener('click', open));
    modal.querySelectorAll('[data-size-guide-close]').forEach((button) => button.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  }

  bindStickyBar() {
    const button = document.querySelector('[data-sticky-add]');
    if (!button) return;
    button.addEventListener('click', () => {
      this.querySelector('[data-add-button]').click();
    });
  }

  /* ----------------------------------------------------------------- submit */

  bindSubmit() {
    if (!this.form) return;

    this.form.addEventListener('submit', (event) => {
      if (this.mode !== 'custom') return;

      const first = this.querySelector('.upload-item[data-slot="0"] [data-upload-input]');
      if (first && !first.files.length) {
        event.preventDefault();
        const error = this.querySelector('.upload-item[data-slot="0"] [data-upload-error]');
        if (error) {
          error.textContent = 'Add a photo of your companion to continue.';
          error.classList.add('visible');
        }
        first.closest('.upload-zone').scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const second = this.querySelector('.upload-item[data-slot="1"]');
      if (second && !second.hidden) {
        const input = second.querySelector('[data-upload-input]');
        if (input && !input.files.length) {
          event.preventDefault();
          const error = second.querySelector('[data-upload-error]');
          if (error) {
            error.textContent = 'Add a photo of your second companion to continue.';
            error.classList.add('visible');
          }
          input.closest('.upload-zone').scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  clearError(selector) {
    const node = this.querySelector(selector);
    if (node) node.classList.remove('visible');
  }
}

if (!customElements.get('companio-product')) {
  customElements.define('companio-product', CompanioProduct);
}
